/**
 * The domain registry (spec 2026-10-18 AI companion growth architecture,
 * section 7): what each domain id on a tool manifest means. Every manifest's
 * `domains` come from here (validateManifest checks them against it). The tool
 * catalog and both tool indexes group under each domain's heading, and phase 6
 * asks one yes/no decision question per domain from its criterion.
 *
 * Data only, with no imports: companion-api cannot load this package, so it
 * keeps a generated copy (apps/edge-companion-api/src/tools/domains.generated.ts)
 * that its domains.unit.test.ts writes and checks against this file.
 */

export interface Domain {
  /** Snake case: what tool manifests name. */
  id: string
  /** The catalog and index heading. */
  heading: string
  /**
   * The turns that belong to the domain, in plain trader words: the criterion
   * of its yes/no decision question (phase 6).
   */
  criterion: string
  /** Panel types (kebab case) that show the domain, when some do. */
  panels?: string[]
}

/** In index order: a tool sits under its first domain, and groups follow this list. */
export const DOMAINS: readonly Domain[] = [
  {
    id: 'workspace',
    heading: 'Workspace and panels',
    criterion:
      "the trader's screen itself: what is on it, adding, removing or arranging panels, the ticker or date a panel shows, a panel's filters or columns, linking panels, or opening another page of the app",
  },
  {
    id: 'charts',
    heading: 'Charts',
    criterion:
      'a chart on screen: drawing levels, lines, zones or labels on it, changing its timeframe, adding, changing or removing an indicator, or reading exact bars from it',
    panels: ['chart'],
  },
  {
    id: 'screener',
    heading: 'Scanners and screening',
    criterion:
      "finding stocks in today's market that match a setup, or grouping today's market by sector, industry or size: top gappers, low floats up big, which sectors are moving. Not past trading days",
    panels: ['scanner', 'multi-scanner', 'multi-chart', 'sector-heatmap'],
  },
  {
    id: 'my_watchlists',
    heading: 'My watchlists',
    criterion: "the trader's own watchlists: which lists, what is in them, changes to them",
    panels: ['watchlist'],
  },
  {
    id: 'market_data',
    heading: 'Prices and volume',
    criterion:
      "a stock's price and trading, now or over recent sessions: last price, change, gap, high and low, volume, relative volume, daily bars",
    panels: ['key-stats'],
  },
  {
    id: 'news',
    heading: 'News',
    criterion:
      'news headlines and press releases about a stock, the latest ones or those around a given day',
    panels: ['news', 'history-news'],
  },
  {
    id: 'filings',
    heading: 'SEC filings',
    criterion: "a company's SEC filings: 8-Ks, S-1s, 10-Qs, 10-Ks, prospectuses, and what they say",
  },
  {
    id: 'share_structure',
    heading: 'Float and share structure',
    criterion:
      'how many shares a company has and who holds them: float, shares outstanding, short interest, institutional and insider ownership',
    panels: ['key-stats'],
  },
  {
    id: 'dilution',
    heading: 'Dilution',
    criterion:
      'whether a company may issue more shares: shelf registrations, ATM offerings, warrants, convertible notes, cash left and runway, reverse splits',
    panels: ['key-stats'],
  },
  {
    id: 'fundamentals',
    heading: 'Fundamentals and financials',
    criterion:
      "a company's business numbers: revenue, earnings per share, margins, cash flow, growth, market cap, sector and industry, and comparing companies on them",
    panels: ['key-stats'],
  },
  {
    id: 'earnings',
    heading: 'Earnings',
    criterion:
      "a company's earnings reports: results against estimates, and what management said on the earnings call",
  },
  {
    id: 'calendar',
    heading: 'Calendar',
    criterion:
      'the dates of upcoming or recent events: earnings dates, stock splits, economic and macro events',
    panels: ['calendar'],
  },
  {
    id: 'history',
    heading: 'Past trading days',
    criterion:
      "what stocks did on past trading days and how often: a stock's own runs, gaps and fades, past days across the market that match a setup, and the History page's studies",
    panels: [
      'ticker-stats',
      'history-records',
      'history-chart',
      'history-stats',
      'history-comparison',
      'history-time-series',
      'history-scatter',
      'history-bar',
      'history-bar-category',
      'history-donut-category',
      'history-intraday',
      'history-calendar',
      'history-multi-chart',
      'history-sankey',
      'history-scorecard',
      'history-heatmap',
      'history-time-distribution',
    ],
  },
  {
    id: 'warehouse',
    heading: 'Warehouse SQL',
    criterion:
      'a question about past market data so specific that only a custom query over the whole historical database answers it',
  },
  {
    id: 'edgescript',
    heading: 'EdgeScript',
    criterion:
      'writing, fixing, explaining or running an EdgeScript indicator or strategy script, or converting a Pine Script one',
    panels: ['script-editor'],
  },
  {
    id: 'backtest',
    heading: 'Backtests and strategies',
    criterion:
      'building, editing, saving, running or reading a backtest strategy: its rules and nodes, saved strategies, and backtest results',
  },
  {
    id: 'research',
    heading: 'Market analyst',
    criterion:
      'a question about stocks or the market that needs data looked up and answered with sources, rather than a change to the screen or how to use the app',
  },
  {
    id: 'knowledge',
    heading: 'Trading knowledge',
    criterion:
      "what a trading term, measure or column means in general, and ready-made scan, column or strategy recipes. Not one stock's own numbers",
  },
  {
    id: 'app_help',
    heading: 'App help',
    criterion:
      'how to use the Edge to Trade app: where a control is, what a panel or page does, or the steps to do something there yourself',
  },
  {
    id: 'assistant',
    heading: 'The assistant',
    criterion:
      'the assistant itself rather than the market or the app: the language it replies in, or what it can do',
  },
]

/** Every domain id, for validateManifest. */
export const DOMAIN_IDS: ReadonlySet<string> = new Set(DOMAINS.map((d) => d.id))

export function domainById(id: string): Domain | undefined {
  return DOMAINS.find((d) => d.id === id)
}
